/**
 * AIInsights Component
 *
 * Displays AI-generated executive summary for SPLH performance data.
 * Handles loading, success, and error states gracefully.
 *
 * Features:
 * - Loading skeleton with pulsing animation
 * - Success state with formatted summary
 * - Error state with retry option
 * - Timestamp showing when insights were generated
 * - Refresh button for manual regeneration
 * - Graceful degradation (never blocks dashboard)
 *
 * @module pages/common/dashboards/SPLHDashboard/components/AIInsights
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Button,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Summarize as SummaryIcon,
  TrendingUp as TrendingUpIcon,
  Map as MapIcon,
  TrackChanges as FocusIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

/**
 * Section header configuration with icons and colors
 */
const SECTION_CONFIG = {
  brandSummary: {
    title: 'Brand Summary',
    icon: SummaryIcon,
    color: '#7C3AED',
  },
  keyMovements: {
    title: 'Key Movements',
    icon: TrendingUpIcon,
    color: '#059669',
  },
  areaImpact: {
    title: 'Area Impact',
    icon: MapIcon,
    color: '#2563EB',
  },
  recommendedFocus: {
    title: 'Recommended Focus',
    icon: FocusIcon,
    color: '#DC2626',
  },
};

/**
 * Parse AI response into structured sections
 * Handles various formats:
 * - **1. Brand Summary**
 * - **1. Brand Summary:**
 * - 1. Brand Summary
 * - **Brand Summary**
 *
 * @param {string} text - Raw AI response text
 * @returns {Object|null} Parsed sections or null if parsing fails
 */
function parseInsightsSections(text) {
  if (!text || typeof text !== 'string') {
    return null;
  }

  // More flexible patterns to match various header formats
  // Matches: **1. Brand Summary**, **Brand Summary**, 1. Brand Summary, Brand Summary:
  const patterns = {
    brandSummary: /\*{0,2}(?:1\.\s*)?Brand\s*Summary\*{0,2}:?\s*/i,
    keyMovements: /\*{0,2}(?:2\.\s*)?Key\s*Movements\*{0,2}:?\s*/i,
    areaImpact: /\*{0,2}(?:3\.\s*)?Area\s*Impact\*{0,2}:?\s*/i,
    recommendedFocus: /\*{0,2}(?:4\.\s*)?Recommended\s*Focus\*{0,2}:?\s*/i,
  };

  // Check if this looks like structured output (has at least 2 section markers)
  const markerCount = Object.values(patterns).filter(p => p.test(text)).length;
  if (markerCount < 2) {
    return null; // Not structured output, use plain text rendering
  }

  const sections = {};
  const sectionOrder = ['brandSummary', 'keyMovements', 'areaImpact', 'recommendedFocus'];

  // Find positions of each section
  const positions = {};
  for (const key of sectionOrder) {
    const match = text.match(patterns[key]);
    if (match) {
      positions[key] = text.indexOf(match[0]);
    }
  }

  // Extract content for each section
  for (let i = 0; i < sectionOrder.length; i++) {
    const currentKey = sectionOrder[i];
    const currentPos = positions[currentKey];

    if (currentPos === undefined) continue;

    // Find the end of this section (start of next section or end of text)
    let endPos = text.length;
    for (let j = i + 1; j < sectionOrder.length; j++) {
      const nextPos = positions[sectionOrder[j]];
      if (nextPos !== undefined && nextPos > currentPos) {
        endPos = nextPos;
        break;
      }
    }

    // Extract and clean the content
    const rawContent = text.substring(currentPos, endPos);
    let content = rawContent.replace(patterns[currentKey], '').trim();

    // Clean up any remaining markdown bold markers
    content = content.replace(/\*\*/g, '');

    if (content) {
      // Parse bullet points if present (handles -, •, *)
      // Check if content has bullet points
      const hasBullets = /^[-•*]\s+|\n\s*[-•*]\s+/.test(content);
      if (hasBullets) {
        // Split on newline, then clean each line
        const bullets = content
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => line.replace(/^[-•*]\s+/, '').trim()) // Remove leading bullet
          .filter(line => line.length > 0);
        sections[currentKey] = bullets.length > 0 ? bullets : content;
      } else {
        sections[currentKey] = content;
      }
    }
  }

  return Object.keys(sections).length > 0 ? sections : null;
}

/**
 * Render a single section with header and content
 */
const InsightSection = ({ sectionKey, content }) => {
  const config = SECTION_CONFIG[sectionKey];
  if (!config || !content) return null;

  const IconComponent = config.icon;
  const isBulletList = Array.isArray(content);

  return (
    <Box sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <IconComponent sx={{ fontSize: 16, color: config.color }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: config.color, fontSize: '0.8rem' }}
        >
          {config.title}
        </Typography>
      </Box>

      {/* Section Content */}
      {isBulletList ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.25 } }}>
          {content.map((item, idx) => (
            <Typography
              key={idx}
              component="li"
              variant="body2"
              sx={{ color: 'text.primary', lineHeight: 1.5, fontSize: '0.85rem' }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'text.primary', lineHeight: 1.5, pl: 0.5, fontSize: '0.85rem' }}
        >
          {content}
        </Typography>
      )}
    </Box>
  );
};

InsightSection.propTypes = {
  sectionKey: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

/**
 * Structured insights component - renders parsed sections
 */
const StructuredInsights = ({ sections }) => {
  const sectionOrder = ['brandSummary', 'keyMovements', 'areaImpact', 'recommendedFocus'];

  return (
    <Box>
      {sectionOrder.map(key => (
        <InsightSection key={key} sectionKey={key} content={sections[key]} />
      ))}
    </Box>
  );
};

StructuredInsights.propTypes = {
  sections: PropTypes.object.isRequired,
};

/**
 * Plain text insights - fallback for unstructured responses
 */
const PlainTextInsights = ({ summary }) => (
  <Typography
    variant="body2"
    sx={{
      lineHeight: 1.7,
      color: 'text.primary',
      whiteSpace: 'pre-wrap',
    }}
  >
    {summary}
  </Typography>
);

/**
 * Loading skeleton component
 */
const LoadingSkeleton = () => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Skeleton variant="circular" width={24} height={24} />
      <Skeleton variant="text" width={120} height={24} />
    </Box>
    <Skeleton variant="text" width="100%" height={20} />
    <Skeleton variant="text" width="100%" height={20} />
    <Skeleton variant="text" width="90%" height={20} />
    <Skeleton variant="text" width="75%" height={20} />
    <Box sx={{ mt: 2 }}>
      <Skeleton variant="text" width={150} height={16} />
    </Box>
  </Box>
);

/**
 * Error state component
 */
const ErrorState = ({ errorMessage, onRetry }) => (
  <Alert
    severity="info"
    icon={<InfoIcon />}
    action={
      onRetry && (
        <Button color="inherit" size="small" onClick={onRetry} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      )
    }
    sx={{
      '& .MuiAlert-message': {
        flex: 1,
      },
    }}
  >
    <Typography variant="body2">
      {errorMessage || 'AI insights are temporarily unavailable.'}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
      The dashboard will continue to work without AI-generated insights.
    </Typography>
  </Alert>
);

ErrorState.propTypes = {
  errorMessage: PropTypes.string,
  onRetry: PropTypes.func,
};

/**
 * Success state component with AI summary
 * Supports both structured (4-section) and plain text formats
 */
const SuccessState = ({ summary, generatedAt, onRefresh }) => {
  // Format timestamp
  let formattedTime = '';
  try {
    if (generatedAt) {
      formattedTime = format(parseISO(generatedAt), 'MMM d, yyyy h:mm a');
    }
  } catch {
    formattedTime = generatedAt || '';
  }

  // Try to parse structured sections
  const parsedSections = React.useMemo(() => parseInsightsSections(summary), [summary]);
  const isStructured = parsedSections !== null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: '#7C3AED', fontSize: 24 }} />
          <Typography variant="subtitle1" fontWeight={600} color="primary.main">
            RadixDash Intelligence
          </Typography>
        </Box>
        {onRefresh && (
          <Tooltip title="Regenerate insights">
            <IconButton size="small" onClick={onRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Summary content - structured or plain text */}
      {isStructured ? (
        <StructuredInsights sections={parsedSections} />
      ) : (
        <PlainTextInsights summary={summary} />
      )}

      {/* Timestamp */}
      {formattedTime && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 2 }}
        >
          Generated: {formattedTime}
        </Typography>
      )}
    </Box>
  );
};

SuccessState.propTypes = {
  summary: PropTypes.string.isRequired,
  generatedAt: PropTypes.string,
  onRefresh: PropTypes.func,
};

/**
 * Unavailable state (AI not called yet)
 */
const UnavailableState = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 3,
      bgcolor: '#fafafa',
      borderRadius: 1,
    }}
  >
    <AIIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
    <Typography variant="body2" color="text.secondary">
      AI insights will appear here
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
      Loading data...
    </Typography>
  </Box>
);

/**
 * AIInsights Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.insights - AI insights response from hook
 * @param {string} props.insights.summary - AI-generated summary text
 * @param {boolean} props.insights.available - Whether insights are available
 * @param {string} props.insights.errorMessage - Error message if unavailable
 * @param {string} props.insights.generatedAt - ISO timestamp of generation
 * @param {boolean} [props.loading=false] - Whether insights are loading
 * @param {Function} [props.onRefresh] - Callback to refresh insights
 * @param {boolean} [props.showCard=true] - Whether to wrap in a card
 * @returns {React.Component} AIInsights component
 */
const AIInsights = ({
  insights,
  loading = false,
  onRefresh,
  showCard = true,
}) => {
  const content = () => {
    // Loading state
    if (loading) {
      return <LoadingSkeleton />;
    }

    // No insights data yet
    if (!insights) {
      return <UnavailableState />;
    }

    // Error state (AI call failed or unavailable)
    if (!insights.available) {
      return (
        <ErrorState
          errorMessage={insights.errorMessage}
          onRetry={onRefresh}
        />
      );
    }

    // Success state
    if (insights.summary) {
      return (
        <SuccessState
          summary={insights.summary}
          generatedAt={insights.generatedAt}
          onRefresh={onRefresh}
        />
      );
    }

    // Fallback
    return <UnavailableState />;
  };

  if (!showCard) {
    return content();
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid',
        borderColor: insights?.available ? 'rgba(124, 58, 237, 0.2)' : 'transparent',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {content()}
      </CardContent>
    </Card>
  );
};

AIInsights.propTypes = {
  /** AI insights response from hook */
  insights: PropTypes.shape({
    summary: PropTypes.string,
    available: PropTypes.bool,
    errorMessage: PropTypes.string,
    generatedAt: PropTypes.string,
  }),
  /** Whether insights are loading */
  loading: PropTypes.bool,
  /** Callback to refresh insights */
  onRefresh: PropTypes.func,
  /** Whether to wrap in a card */
  showCard: PropTypes.bool,
};

export default AIInsights;
